import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, getDoc, query, where } from 'firebase/firestore';

const isDev = Boolean((import.meta as any).env?.DEV);
const logDebug = (...args: any[]) => {
  if (isDev) console.log(...args);
};

export interface GeniusLociData {
  id: string;
  name: string;
  avatar?: string;
  voice: string;
  personality: string;
  specialties: string[];
  bio: string;
  quote: string;
}

export interface PromptData {
  id: string;
  geniusLociId: string;
  initialMessage: string;
  context: string;
  voice: string;
  personality: string;
  specialties: string[];
  bio: string;
  quote: string;
  avatar?: string;
  guidelines?: Record<string, any>;
}

/**
 * Sincronizza i dati del Genius Loci con il Prompt corrispondente
 * Questo garantisce che le informazioni di personalità siano sempre aggiornate
 */
export async function syncGeniusLociToPrompt(geniusLociId: string): Promise<boolean> {
  try {
    logDebug(`🔄 [SYNC] Sincronizzazione Genius Loci ${geniusLociId} con Prompt...`);

    // 1. Carica i dati del Genius Loci
    const geniusLociDoc = await getDoc(doc(db, 'geniusLoci', geniusLociId));
    if (!geniusLociDoc.exists()) {
      console.error(`❌ [SYNC] Genius Loci ${geniusLociId} non trovato`);
      return false;
    }

    const geniusLociData = geniusLociDoc.data() as GeniusLociData;
    logDebug(`👤 [SYNC] Genius Loci: ${geniusLociData.name}, Personality: ${geniusLociData.personality}`);

    // 2. Cerca il Prompt corrispondente usando geniusLociId
    const promptsQuery = query(collection(db, 'prompts'), where('geniusLociId', '==', geniusLociId));
    const promptsSnapshot = await getDocs(promptsQuery);
    
    if (!promptsSnapshot.empty) {
      // Aggiorna SOLO il Prompt esistente
      const promptDoc = promptsSnapshot.docs[0];
      const promptData = promptDoc.data() as PromptData;
      logDebug(`📝 [SYNC] Aggiornamento Prompt esistente ID: ${promptDoc.id}`);

      const updatedPromptData = {
        geniusLociId: geniusLociId,
        // Sincronizza i dati del Genius Loci
        voice: geniusLociData.voice,
        personality: geniusLociData.personality,
        specialties: geniusLociData.specialties || [],
        bio: geniusLociData.bio,
        quote: geniusLociData.quote,
        avatar: geniusLociData.avatar,
        // Mantieni o genera i dati specifici del prompt
        initialMessage: promptData.initialMessage || generateInitialMessage(geniusLociData),
        context: promptData.context || generateContext(geniusLociData),
        guidelines: promptData.guidelines || generateGuidelines(geniusLociData),
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'prompts', promptDoc.id), updatedPromptData);
      logDebug(`✅ [SYNC] Prompt aggiornato: Personality="${updatedPromptData.personality}"`);
    } else {
      // NON creare un nuovo Prompt - solo logga un avviso
      logDebug(`⚠️ [SYNC] Nessun Prompt trovato per Genius Loci ${geniusLociData.name} (ID: ${geniusLociId})`);
      logDebug(`⚠️ [SYNC] I Prompt devono essere creati manualmente dalla sezione Prompt`);
      return false;
    }

    return true;
  } catch (error) {
    console.error(`❌ [SYNC] Errore durante la sincronizzazione:`, error);
    return false;
  }
}

/**
 * Sincronizza tutti i Genius Loci con i loro Prompt corrispondenti
 */
export async function syncAllGeniusLociToPrompts(): Promise<{ success: number; errors: number }> {
  try {
    logDebug(`🔄 [SYNC] Sincronizzazione di tutti i Genius Loci...`);

    const geniusLociSnapshot = await getDocs(collection(db, 'geniusLoci'));
    let successCount = 0;
    let errorCount = 0;

    for (const doc of geniusLociSnapshot.docs) {
      const success = await syncGeniusLociToPrompt(doc.id);
      if (success) {
        successCount++;
      } else {
        errorCount++;
      }
    }

    logDebug(`✅ [SYNC] Completato: ${successCount} successi, ${errorCount} errori`);
    return { success: successCount, errors: errorCount };
  } catch (error) {
    console.error(`❌ [SYNC] Errore durante la sincronizzazione globale:`, error);
    return { success: 0, errors: 1 };
  }
}

/**
 * Genera un messaggio iniziale basato sui dati del Genius Loci
 */
function generateInitialMessage(geniusLoci: GeniusLociData): string {
  const personality = (geniusLoci.personality || '').toLowerCase();
  const opening = getOpeningByPersonality(personality);
  const bio = geniusLoci.bio?.trim() ? `${geniusLoci.bio.trim()}.` : '';
  const specialties = (geniusLoci.specialties || []).filter(Boolean);
  const specialtiesLine = specialties.length > 0
    ? ` Posso guidarti tra ${specialties.slice(0, 2).join(' e ')}.`
    : ' Posso guidarti tra storie, luoghi e dettagli nascosti del territorio.';
  const quote = geniusLoci.quote?.trim() ? ` "${geniusLoci.quote.trim()}"` : '';

  return `${opening} Sono ${geniusLoci.name}.${bio ? ` ${bio}` : ''}${specialtiesLine}${quote} Da dove vuoi cominciare?`;
}

function getOpeningByPersonality(personality: string): string {
  if (personality.includes('saggio')) return 'Pace a te, viandante.';
  if (personality.includes('ribelle')) return 'Qui la memoria non si inchina: prende voce.';
  if (personality.includes('custode')) return 'Fermati un istante: questo luogo conserva tracce profonde.';
  if (personality.includes('mago')) return 'Guarda bene: ogni dettaglio puo rivelare una trama nascosta.';
  if (personality.includes('angelo')) return 'Ti accompagno passo dopo passo, con uno sguardo amico.';
  if (personality.includes('creatore')) return 'Ogni pietra qui e materia viva di racconto.';
  if (personality.includes('esploratore')) return 'Apri lo sguardo: l\'orizzonte ha sempre un sentiero in piu.';
  if (personality.includes('sovrano')) return 'Benvenuto: qui la storia prende forma e ordine.';
  return 'Benvenuto.';
}

/**
 * Genera il contesto basato sui dati del Genius Loci
 */
function generateContext(geniusLoci: GeniusLociData): string {
  return `Sono ${geniusLoci.name}, ${geniusLoci.personality.toLowerCase()}. ${geniusLoci.bio}. Le mie specialità sono: ${geniusLoci.specialties.join(', ')}. ${geniusLoci.quote ? `Come dico sempre: "${geniusLoci.quote}"` : ''}`;
}

/**
 * Genera le linee guida basate sui dati del Genius Loci
 */
function generateGuidelines(geniusLoci: GeniusLociData): Record<string, any> {
  return {
    tone: geniusLoci.voice,
    language: 'Italiano colloquiale e accogliente',
    role: `Guida esperta del territorio con personalità ${geniusLoci.personality.toLowerCase()}`,
    interaction_style: 'Conversazionale e coinvolgente',
    user_engagement: 'Sempre disponibile a condividere conoscenze e storie del territorio'
  };
}

/**
 * Verifica se un Genius Loci è sincronizzato con il suo Prompt
 */
export async function isGeniusLociSynced(geniusLociId: string): Promise<boolean> {
  try {
    const geniusLociDoc = await getDoc(doc(db, 'geniusLoci', geniusLociId));
    if (!geniusLociDoc.exists()) {
      return false;
    }

    // Cerca il Prompt usando geniusLociId
    const promptsQuery = query(collection(db, 'prompts'), where('geniusLociId', '==', geniusLociId));
    const promptsSnapshot = await getDocs(promptsQuery);
    
    if (promptsSnapshot.empty) {
      return false;
    }

    const geniusLociData = geniusLociDoc.data() as GeniusLociData;
    const promptData = promptsSnapshot.docs[0].data() as PromptData;

    // Verifica se i dati chiave sono sincronizzati
    return (
      geniusLociData.voice === promptData.voice &&
      geniusLociData.personality === promptData.personality &&
      geniusLociData.bio === promptData.bio &&
      geniusLociData.quote === promptData.quote &&
      JSON.stringify(geniusLociData.specialties) === JSON.stringify(promptData.specialties)
    );
  } catch (error) {
    console.error(`❌ [SYNC] Errore durante la verifica:`, error);
    return false;
  }
}
